import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ServiceBase, OrigamiHttpResponse, OrigamiResponse } from './base/service.base';
import { DocumentViewerType, DirectoryType, ViewerType } from '@app/origami-api/constants';
import { BuildProps } from '@app/origami-api/build-props';
import { ErrorRecord } from '@app/origami-api/utils/error-utils';
import { ErrorCode } from '@app/origami-api/constants/error-codes';

export interface FileInfo {
  TopicId?: number;
  FieldId?: number;
  FileId?: number;
  Version?: string;
  Label?: string;
  IsDraftVersion?: boolean;
  IsApprovedVersion?: boolean;
  IsEffectiveVersion?: boolean;
  DirectoryType?: number;
  FilePath?: string;
  FileName?: string;
  SiteId?: number;
  FileToken?: string;
}

export interface GetTopicFileViewerUrlParams {
  fileInfos: FileInfo[];
  siteId: number;
  viewerType: number;
  paramAddition?: string;
  promptDownload?: boolean;
  silentErrorCodes?: ErrorCode[];
}

export interface ApplyRedactionParams {
  siteId: number;
  documentId: number;
}

export interface ApplyRedactionResult {
  LockedUserId: number;
  LockedUserName: number;
}

export interface DiscardRedactionParams {
  siteId: number;
  documentId: number;
}

export interface DiscardRedactionResult {
  LockedUserId: number;
  LockedUserName: number;
}

export interface ResetRedactionParams {
  siteId: number;
  documentId: number;
}

export interface ResetRedactionResult {
  LockedUserId: number;
  LockedUserName: number;
}

export interface FileViewerInfo {
  DocumentId: string;
  FileHash: string;
}

export interface TopicFileViewerInfo {
  FileInfo?: FileViewerInfo[];
  ViewerUrl?: string;
  LockedUserId?: number;
  LockedUserName?: string;
  ErrorRecords?: ErrorRecord[];
  ViewerData?: string;
  ViewerType?: ViewerType;
}

export enum RejectedDocumentResolveType {
  manualEdit = 1,
  regenerate = 2,
}

export interface ResolveRejectedDocumentParams {
  siteId: number;
  documentId: number;
  resolveType: RejectedDocumentResolveType;
}

export interface GetTopicFilePreviewResult {
  ThumbnailFileUrl: string;
  ThumbnailStatusId: number;
}

export const DOCUMENT_MANAGER_SERVICE_URL = 'DocumentManagerService.svc';

@Injectable()
export class DocumentManagerService extends ServiceBase {
  serviceUrl = DOCUMENT_MANAGER_SERVICE_URL;
  noLogoUrl = 'assets/media/no_logo.gif';

  getTopicFilePreview(body, silentErrorCodes?): Observable<OrigamiResponse<GetTopicFilePreviewResult>> {
    return this.origamiPost('/GetTopicFilePreview', body, null, silentErrorCodes, true);
  }

  getFileViewerUrl(
    siteId: number,
    directoryType: DirectoryType,
    filePath: string,
    fileName: string,
  ): Observable<OrigamiResponse<string>> {
    const body = {
      siteId: siteId,
      directoryType: directoryType,
      filePath: filePath,
      fileName: fileName,
      viewerType: DocumentViewerType.ARender,
      promptDownload: false,
    };
    return this.origamiPost('/GetFileViewerUrl', body, null, null, true);
  }

  //@deprecated
  getTopicFileViewerUrl(body: GetTopicFileViewerUrlParams): Observable<OrigamiResponse<string>> {
    return this.origamiPost('/GetTopicFileViewerUrl', body, null, null, true);
  }

  getTopicFileViewerInfo(body: GetTopicFileViewerUrlParams): Observable<OrigamiResponse<TopicFileViewerInfo>> {
    body.paramAddition += `&v=${BuildProps.OrigamiVersion}`;
    return this.origamiPost('/GetTopicFileViewerInfo', body, null, body?.silentErrorCodes || null, true);
  }

  applyRedaction(body: ApplyRedactionParams): Observable<OrigamiResponse<ApplyRedactionResult>> {
    return this.origamiPost('/ApplyRedaction', body, null, null, true);
  }

  discardRedaction(body: DiscardRedactionParams): Observable<OrigamiResponse<DiscardRedactionResult>> {
    return this.origamiPost('/DiscardRedaction', body, null, null, true);
  }

  resetRedaction(body: ResetRedactionParams): Observable<OrigamiResponse<ResetRedactionResult>> {
    return this.origamiPost('/ResetRedaction', body, null, null, true);
  }

  // TODO: think how do it by better way
  downloadSignCertificate(envelopeId: string, siteId: number) {
    const a = document.createElement('a');
    a.href = `${BuildProps.origin}/${this.serviceUrl}/GetSignCertificate/${siteId}/${envelopeId}/SaveFile`;
    a.target = '_blank';

    document.body.appendChild(a);

    a.addEventListener('click', (e) => {
      a.parentNode.removeChild(a);
    });

    a.click();
  }

  resolveRejectedDocument(body: ResolveRejectedDocumentParams): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ResolveRejectedDocument', body, null, null, true);
  }
}
